import { Link } from 'react-router-dom';
import { toFullUrl } from '../../config/api';
import { ExternalLink, Users } from 'lucide-react';

export function AboutSection({ artistProfile, siteSettings, isVisible }) {
  if (!isVisible('about') || !artistProfile || (!artistProfile.bio && !artistProfile.profile_image)) return null;

  return (
    <section className="border-t border-[#262626] py-16 sm:py-24 px-4 sm:px-8 bg-[#0A0A0A]" data-testid="about-section">
      <div className="max-w-5xl mx-auto">
        <p className="overline text-[var(--primary,#FF3B30)] mb-4">{siteSettings?.section_titles?.about_label || 'THE ARTIST'}</p>
        <div className="flex flex-col md:flex-row items-center gap-12">
          {artistProfile.profile_image && (
            <div className="flex-shrink-0">
              <div className="w-64 h-64 border border-[#262626] overflow-hidden">
                <img
                  src={toFullUrl(artistProfile.profile_image)}
                  alt={artistProfile.stage_name || 'Artist'}
                  className="w-full h-full object-cover"
                  data-testid="about-profile-image"
                />
              </div>
            </div>
          )}
          <div className="flex-1 text-center md:text-left">
            {artistProfile.stage_name && (
              <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-4">
                {artistProfile.stage_name}
              </h2>
            )}
            {artistProfile.bio && (
              <p className="text-[#A3A3A3] text-lg leading-relaxed whitespace-pre-line" data-testid="about-bio-text">
                {artistProfile.bio}
              </p>
            )}
            {artistProfile.social_links && Object.keys(artistProfile.social_links).length > 0 && (
              <div className="flex gap-4 mt-6 justify-center md:justify-start">
                {Object.entries(artistProfile.social_links).map(([platform, url]) => {
                  if (!url) return null;
                  return (
                    <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="text-[#A3A3A3] hover:text-[var(--primary,#FF3B30)] transition-colors">
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function FeaturedArtistsSection({ featuredArtists, siteSettings, isVisible }) {
  if (!isVisible('featured') || featuredArtists.length === 0) return null;

  return (
    <section className="border-t border-[#262626] py-16 sm:py-24 px-4 sm:px-8" data-testid="featured-artists-section" id="featured-artists">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="overline text-[var(--primary,#FF3B30)] mb-2">{siteSettings?.section_titles?.featured_label || 'SPOTLIGHT'}</p>
            <h2 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter">
              {siteSettings?.section_titles?.featured || 'FEATURED ARTISTS'}
            </h2>
          </div>
          <Users className="w-8 h-8 text-[var(--primary,#FF3B30)]" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {featuredArtists.map(artist => (
            <div key={artist.id} className="group" data-testid={`featured-artist-${artist.id}`}>
              {artist.has_profile_page ? (
                <Link to={`/artist/${artist.slug}`} className="block">
                  <div className="border border-[#262626] overflow-hidden bg-[#141414] hover:border-[var(--accent,#DAA520)]/30 transition-colors">
                    <div className="aspect-square bg-[#1A1A1A] overflow-hidden">
                      {artist.photo ? (
                        <img src={toFullUrl(artist.photo)} alt={artist.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Users className="w-12 h-12 text-[#333]" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <h3 className="font-cabinet font-bold text-sm uppercase truncate group-hover:text-[var(--accent,#DAA520)] transition-colors">{artist.name}</h3>
                      {artist.bio && <p className="text-xs text-[#666] mt-0.5 line-clamp-2">{artist.bio}</p>}
                    </div>
                  </div>
                </Link>
              ) : (
                <div className="border border-[#262626] overflow-hidden bg-[#141414]">
                  <div className="aspect-square bg-[#1A1A1A] overflow-hidden">
                    {artist.photo ? (
                      <img src={toFullUrl(artist.photo)} alt={artist.name}
                        className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Users className="w-12 h-12 text-[#333]" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-cabinet font-bold text-sm uppercase truncate">{artist.name}</h3>
                    {artist.bio && <p className="text-xs text-[#666] mt-0.5 line-clamp-2">{artist.bio}</p>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
